import Modal from "@via/components/modal.tsx";
import { Spinner } from "@via/components/loading/loading.tsx";
import DeleteModal from "@via/components/delete-modal/delete-modal.tsx";
import { ModalSize } from "@via/utils/constants.tsx";
import FormInputComponent from "@via/components/inputs/form-input-component";
import SelectComponent from "@via/components/select/select.tsx";
import {
  ORG_GOOGLE_USAGE_SCOPE_OPTIONS,
  useCreateOrganizationGoogleCredential,
  useDeleteOrganizationGoogleCredential,
  useGetOrganizationAllowedServiceAccounts,
  useGetOrganizationGoogleCredentials,
  useUpdateOrganizationGoogleCredential,
} from "@via/pages/organization/organization.api.tsx";
import { type JSX, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { GoogleCredentialUsageScope } from "common";
import {
  maxLengthValidation,
  requiredValidation,
} from "@via/utils/form-validators.tsx";

type FormState = {
  name: string;
  serviceAccountEmail: string;
  usageScope: GoogleCredentialUsageScope;
};

const DEFAULT_FORM: FormState = {
  name: "",
  serviceAccountEmail: "",
  usageScope: GoogleCredentialUsageScope.Dataset,
};

export default function ImpersonateServiceAccountModal(props: {
  organizationId: string;
  open: boolean;
  setOpen: (open: boolean) => void;
}): JSX.Element {
  const { organizationId, open, setOpen } = props;

  const { data: credentials = [], isLoading } =
    useGetOrganizationGoogleCredentials(
      organizationId,
      { usageScope: GoogleCredentialUsageScope.Dataset },
      { enabled: open && !!organizationId }
    );

  const {
    data: allowedServiceAccounts = [],
    isLoading: isAllowedServiceAccountsLoading,
  } = useGetOrganizationAllowedServiceAccounts(organizationId, {
    enabled: open && !!organizationId,
  });

  const { mutateAsync: createCredential, isPending: isCreating } =
    useCreateOrganizationGoogleCredential();
  const { mutateAsync: updateCredential, isPending: isUpdating } =
    useUpdateOrganizationGoogleCredential();
  const { mutateAsync: deleteCredential, isPending: isDeleting } =
    useDeleteOrganizationGoogleCredential();

  const [editingId, setEditingId] = useState<number | null>(null);
  const [credentialToDelete, setCredentialToDelete] = useState<{
    id: number;
    name: string;
  } | null>(null);

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isValid },
  } = useForm<FormState>({
    mode: "onChange",
    defaultValues: DEFAULT_FORM,
  });

  const form = watch();

  useEffect(() => {
    if (!open) {
      setEditingId(null);
      setCredentialToDelete(null);
      reset(DEFAULT_FORM);
    }
  }, [open, reset]);

  const isSaving = isCreating || isUpdating;

  const selectedEmail = (form.serviceAccountEmail ?? "").trim();
  const isSelectedEmailAllowed = () => {
    return (
      selectedEmail.length > 0 && allowedServiceAccounts.includes(selectedEmail)
    );
  };

  const canSubmit =
    !!organizationId && isValid && !isSaving && isSelectedEmailAllowed;

  const serviceAccountOptions = allowedServiceAccounts.map(s => ({
    label: s,
    value: s,
  }));

  const serviceAccountPlaceholder = useMemo(() => {
    if (isAllowedServiceAccountsLoading)
      return "Loading approved service accounts…";
    if (allowedServiceAccounts.length === 0)
      return "No approved service accounts configured";
    return "Select an approved service account";
  }, [allowedServiceAccounts.length, isAllowedServiceAccountsLoading]);

  const serviceAccountSelectValue = useMemo(() => {
    if (!selectedEmail) return null;
    return { label: selectedEmail, value: selectedEmail };
  }, [selectedEmail]);

  const onSubmit = handleSubmit(async payload => {
    if (!organizationId) return;

    const requestBody = {
      name: payload.name.trim(),
      serviceAccountEmail: payload.serviceAccountEmail.trim(),
      usageScope: payload.usageScope,
    };

    if (editingId) {
      await updateCredential({
        organizationId,
        credentialId: editingId,
        requestBody,
      });
    } else {
      await createCredential({ organizationId, requestBody });
    }

    setEditingId(null);
    reset(DEFAULT_FORM);
  });

  const startEdit = (cred: (typeof credentials)[number]) => {
    setEditingId(cred.id);
    reset({
      name: cred.name,
      serviceAccountEmail: cred.serviceAccountEmail,
      usageScope: cred.usageScope,
    });
  };

  return (
    <>
      <Modal
        open={open}
        setOpen={setOpen}
        size={ModalSize.Large}
        width={900}
        title="Configure Impersonate Service Accounts"
        showButtons={false}>
        <div className="space-y-6">
          <div className="rounded-md border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-700">
            Add GCP service accounts for dataset features.
          </div>

          <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
            <div className="px-4 py-3 border-b border-gray-200">
              <h3 className="text-sm font-semibold text-gray-900">
                Organization service accounts
              </h3>
            </div>
            {isLoading ? (
              <div className="p-4">
                <Spinner loading />
              </div>
            ) : credentials.length === 0 ? (
              <div className="p-4 text-sm text-gray-600">
                No service accounts configured yet.
              </div>
            ) : (
              <div className="divide-y divide-gray-200">
                {credentials.map(c => (
                  <div
                    key={c.id}
                    className="flex flex-col gap-2 p-4 sm:flex-row sm:items-center sm:justify-between">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="font-medium text-gray-900 truncate">
                          {c.name}
                        </p>
                        <span className="rounded-full bg-indigo-50 px-2 py-0.5 text-xs font-medium text-indigo-700">
                          {c.usageScope}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 break-all">
                        {c.serviceAccountEmail}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => startEdit(c)}
                        className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50">
                        Edit
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          setCredentialToDelete({ id: c.id, name: c.name })
                        }
                        className="rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 disabled:opacity-50"
                        disabled={isDeleting}>
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="rounded-lg border border-gray-200 bg-white p-4">
            <h3 className="text-sm font-semibold text-gray-900">
              {editingId ? "Update service account" : "Add service account"}
            </h3>
            <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <FormInputComponent
                  control={control}
                  name="name"
                  label="Name"
                  placeholder="e.g. Dataset SA"
                  validations={{
                    ...requiredValidation("Name"),
                    ...maxLengthValidation(
                      45,
                      "Name must be 45 characters or fewer"
                    ),
                  }}
                />
              </div>

              <div className="mt-2">
                <label className="block text-sm font-medium text-gray-900">
                  Usage scope
                </label>
                <div className="mt-2">
                  <SelectComponent
                    control={control}
                    name="usageScope"
                    options={ORG_GOOGLE_USAGE_SCOPE_OPTIONS}
                    enableClear={false}
                    placeholder="Select usage scope"
                    rules={{
                      required: "Usage scope is required",
                    }}
                  />
                </div>
                {errors.usageScope?.message ? (
                  <p className="mt-1 text-xs text-rose-500">
                    {String(errors.usageScope.message)}
                  </p>
                ) : null}
              </div>

              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-gray-900">
                  Service account email
                </label>
                <div className="mt-2">
                  <SelectComponent
                    control={control}
                    name="serviceAccountEmail"
                    options={serviceAccountOptions}
                    enableClear={false}
                    isSearchable
                    placeholder={serviceAccountPlaceholder}
                    isLoading={isAllowedServiceAccountsLoading}
                    readOnly={
                      isAllowedServiceAccountsLoading ||
                      allowedServiceAccounts.length === 0
                    }
                    value={serviceAccountSelectValue}
                    rules={{
                      required: "Service account email is required",
                      validate: (value: string) => {
                        const trimmed = (value ?? "").trim();
                        if (!trimmed)
                          return "Service account email is required";
                        return (
                          allowedServiceAccounts.some(s => s === trimmed) ||
                          "Service account email is not approved by system admin."
                        );
                      },
                    }}
                  />
                </div>

                {errors.serviceAccountEmail?.message ? (
                  <p className="mt-1 text-xs text-rose-500">
                    {String(errors.serviceAccountEmail.message)}
                  </p>
                ) : selectedEmail && !isSelectedEmailAllowed ? (
                  <p className="mt-1 text-xs text-rose-500">
                    {selectedEmail} is not approved by system admin.
                  </p>
                ) : null}

                {allowedServiceAccounts.length === 0 &&
                !isAllowedServiceAccountsLoading ? (
                  <p className="mt-1 text-xs text-gray-500">
                    No approved service accounts configured by system admin.
                  </p>
                ) : null}
              </div>
            </div>

            <div className="mt-4 flex items-center justify-end gap-2">
              {editingId ? (
                <button
                  type="button"
                  onClick={() => {
                    setEditingId(null);
                    reset(DEFAULT_FORM);
                  }}
                  className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50">
                  Cancel edit
                </button>
              ) : null}
              <button
                type="button"
                onClick={() => void onSubmit()}
                disabled={!canSubmit}
                className="inline-flex justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:opacity-50">
                <Spinner loading={isSaving}>
                  {editingId ? "Update" : "Add"}
                </Spinner>
              </button>
            </div>
          </div>
        </div>
      </Modal>

      {credentialToDelete && (
        <DeleteModal
          item={credentialToDelete}
          setItem={setCredentialToDelete}
          title="Delete service account"
          confirmText="Delete"
          message={() => (
            <div className="space-y-2 text-sm text-gray-700">
              <p>
                This will permanently delete{" "}
                <strong>{credentialToDelete.name}</strong>.
              </p>
              <p className="text-red-600">
                Any dataset configurations that reference this credential will
                stop working until updated.
              </p>
            </div>
          )}
          isLoading={isDeleting}
          deleteFunction={async () => {
            await deleteCredential({
              organizationId,
              credentialId: credentialToDelete.id,
            });
          }}
        />
      )}
    </>
  );
}
